import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache globale per le immagini già risolte
const imageCache = new Map();
const inflight = new Map();

/**
 * Hook per ottenere l'immagine di un veicolo dal servizio Drive.
 * Usa cache in memoria per evitare richieste duplicate.
 */
export function useVehicleImage(marca, modello) {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const { getAuthHeader } = useAuth();
  const abortRef = useRef(null);

  useEffect(() => {
    if (!marca || !modello) {
      setImageUrl(null);
      return;
    }

    const cacheKey = `${marca}_${modello}`.toLowerCase();
    
    // Check cache
    if (imageCache.has(cacheKey)) {
      setImageUrl(imageCache.get(cacheKey));
      return;
    }

    let cancelled = false;
    setLoading(true);

    // Promessa condivisa per cacheKey: card con lo stesso modello fanno una sola richiesta
    if (!inflight.has(cacheKey)) {
      inflight.set(cacheKey, axios.get(
        `${API}/drive-images/match?marca=${encodeURIComponent(marca)}&modello=${encodeURIComponent(modello)}`,
        { headers: getAuthHeader() }
      ).then((res) => (res.data.found ? res.data.thumbnail_url : null))
        .catch(() => null)
        .then((url) => { imageCache.set(cacheKey, url); inflight.delete(cacheKey); return url; }));
    }
    const fetchImage = async () => {
      const url = await inflight.get(cacheKey);
      if (!cancelled) {
        setImageUrl(url);
        setLoading(false);
      }
    };

    fetchImage();
    return () => { cancelled = true; };
  }, [marca, modello]);

  return { imageUrl, loading };
}

/**
 * Hook per batch di immagini (più efficiente per liste).
 */
export function useVehicleImagesBatch(vehicles) {
  const [images, setImages] = useState({});
  const { getAuthHeader } = useAuth();
  const prevKeyRef = useRef('');

  useEffect(() => {
    if (!vehicles || vehicles.length === 0) return;

    // Filtra solo veicoli non in cache
    const toFetch = [];
    const cached = {};
    
    for (const v of vehicles) {
      if (!v.marca || !v.modello) continue;
      const key = `${v.marca}_${v.modello}`.toLowerCase();
      if (imageCache.has(key)) {
        cached[key] = imageCache.get(key);
      } else {
        toFetch.push(v);
      }
    }

    // Se tutto è in cache, usa direttamente
    const batchKey = vehicles.map(v => `${v.marca}_${v.modello}`).join('|');
    if (toFetch.length === 0) {
      if (batchKey !== prevKeyRef.current) {
        setImages(cached);
        prevKeyRef.current = batchKey;
      }
      return;
    }

    let cancelled = false;

    const fetchBatch = async () => {
      try {
        const res = await axios.post(
          `${API}/drive-images/batch-match`,
          { vehicles: toFetch.map(v => ({ marca: v.marca, modello: v.modello })) },
          { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } }
        );
        if (!cancelled) {
          const matches = res.data.matches || {};
          const result = { ...cached };
          for (const [key, val] of Object.entries(matches)) {
            const cacheKey = key.toLowerCase();
            imageCache.set(cacheKey, val.thumbnail_url);
            result[cacheKey] = val.thumbnail_url;
          }
          // Mark non-found as null in cache
          for (const v of toFetch) {
            const cacheKey = `${v.marca}_${v.modello}`.toLowerCase();
            if (!result[cacheKey]) {
              imageCache.set(cacheKey, null);
            }
          }
          setImages(result);
          prevKeyRef.current = batchKey;
        }
      } catch {
        if (!cancelled) setImages(cached);
      }
    };

    fetchBatch();
    return () => { cancelled = true; };
  }, [vehicles?.length]);

  return images;
}

/**
 * Componente immagine veicolo con fallback e disclaimer.
 */
export function VehicleImage({ marca, modello, className = '', showDisclaimer = false, size = 'thumbnail' }) {
  const { imageUrl } = useVehicleImage(marca, modello);

  if (!imageUrl) return null;

  return (
    <div className={`relative ${className}`}>
      <img
        src={imageUrl}
        alt={`${marca} ${modello}`}
        className="w-full h-full object-contain"
        loading="lazy"
        onError={(e) => { e.target.style.display = 'none'; }}
      />
      {showDisclaimer && (
        <p className="text-[9px] text-slate-400 italic text-center mt-0.5 leading-tight">
          Immagine a scopo illustrativo, potrebbe non essere conforme all'allestimento specifico
        </p>
      )}
    </div>
  );
}
